import {
  addHours,
  eachDayOfInterval,
  eachHourOfInterval,
  eachMinuteOfInterval,
  endOfDay,
  format,
  getMinutes,
  isAfter,
  isSameDay,
  isWithinInterval,
  roundToNearestMinutes,
  set,
  startOfDay,
  startOfHour,
} from 'date-fns';

export const isSameCalendarDay = isSameDay;

// "10:30 AM" at body-xs needs about this much room. Below that, a session
// of 14 half-hour ticks collides — fall back to the hourly set.
export const INTRADAY_HALF_HOUR_TICK_MIN_PX = 48;

export const getSingleDayHourlyTickDates = (startDate: Date, endDate: Date) => {
  const start = startOfHour(addHours(startDate, 1));
  if (isAfter(start, endDate)) {
    return [];
  }

  return eachHourOfInterval({ start, end: endDate });
};

export const getSingleDayHalfHourlyTickDates = (
  startDate: Date,
  endDate: Date
) => {
  const start = roundToNearestMinutes(startDate, {
    nearestTo: 30,
    roundingMethod: 'ceil',
  });
  if (isAfter(start, endDate)) {
    return [];
  }

  return eachMinuteOfInterval({ start, end: endDate }, { step: 30 });
};

export const getIntradaySessionTickDates = (
  startDate: Date,
  endDate: Date,
  plotWidth: number
) => {
  const halfHourTicks = getSingleDayHalfHourlyTickDates(startDate, endDate);

  if (
    halfHourTicks.length > 0 &&
    plotWidth / halfHourTicks.length >= INTRADAY_HALF_HOUR_TICK_MIN_PX
  ) {
    return halfHourTicks;
  }

  return getSingleDayHourlyTickDates(startDate, endDate);
};

export type CalendarTickGranularity = 'day' | 'week' | 'month';

export const getCalendarTickGranularity = (
  spanDays: number
): CalendarTickGranularity => {
  if (spanDays <= 21) {
    return 'day';
  }

  if (spanDays < 42) {
    return 'week';
  }

  return 'month';
};
export const formatMarketTime = (date: Date) =>
  format(date, getMinutes(date) === 30 ? 'h:mm a' : 'h a');

export const getMarketOpenAndCloseTickTimestamps = (
  domainMin: number,
  domainMax: number
) => {
  const startDate = new Date(domainMin);
  const endDate = new Date(domainMax);
  const firstDay = startOfDay(startDate);
  const lastDay = endOfDay(endDate);

  if (isAfter(firstDay, lastDay)) {
    return [];
  }

  return eachDayOfInterval({ start: firstDay, end: lastDay }).flatMap(day => {
    const open = set(day, {
      hours: 9,
      minutes: 30,
      seconds: 0,
      milliseconds: 0,
    });
    const close = set(day, {
      hours: 16,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });

    return [open, close]
      .filter(tick =>
        isWithinInterval(tick, { start: startDate, end: endDate })
      )
      .map(tick => tick.getTime());
  });
};
